import { createPopper, type Instance, type Modifier } from '@popperjs/core';
import { useEffect, useRef, useState } from 'react';

const POPOUT_PREFERRED_WIDTH = 800;
const POPOUT_MAP_PEEK_WIDTH = 45;
const VIEWPORT_PADDING = 16;

interface UsePopoverPositioningParams<T extends HTMLElement> {
  anchorRef: React.MutableRefObject<T | null>;
  popperElement: HTMLDivElement | null;
  isOpen: boolean;
  gridRef?: {
    getScrollLeft: () => number;
    getDOMNode: () => HTMLDivElement | null;
  };
  boundaryRef?: React.RefObject<HTMLDivElement>;
}

interface UsePopoverPositioningReturn {
  placement: string;
  chevronTop: number | null;
  maxWidth: number;
  popoverTop: number;
  popoverHeight: number;
  rowHeight: number | null;
}

export const usePopoverPositioning = <T extends HTMLElement>({
  anchorRef,
  popperElement,
  isOpen,
  gridRef,
  boundaryRef,
}: UsePopoverPositioningParams<T>): UsePopoverPositioningReturn => {
  const [placement, setPlacement] = useState<string>('right');
  const [chevronTop, setChevronTop] = useState<number | null>(null);
  const [maxWidth, setMaxWidth] = useState<number>(POPOUT_PREFERRED_WIDTH);
  const [popoverTop, setPopoverTop] = useState<number>(0);
  const [popoverHeight, setPopoverHeight] = useState<number>(0);
  const [rowHeight, setRowHeight] = useState<number | null>(null);
  const popperInstanceRef = useRef<Instance | null>(null);

  useEffect(() => {
    if (!anchorRef.current || !popperElement || !isOpen) {
      return;
    }

    const adjustToTableEdgeModifier: Modifier<
      'adjustToTableEdge',
      Record<string, unknown>
    > = {
      name: 'adjustToTableEdge',
      enabled: true,
      phase: 'afterWrite',
      fn: ({ state }) => {
        // Adjust position to be relative to table container's right edge, not row's right edge
        const gridNode = gridRef?.getDOMNode();
        const gridRect = gridNode?.getBoundingClientRect();
        const anchorRect = anchorRef.current?.getBoundingClientRect();
        const boundaryNode = boundaryRef?.current;

        // Find the ChartCard container by traversing up from boundaryRef
        // ChartCard has data-testid="card" attribute
        let chartCardNode: HTMLElement | null = null;
        if (boundaryNode) {
          let parent: HTMLElement | null = boundaryNode.parentElement;
          while (parent) {
            if (parent.getAttribute('data-testid') === 'card') {
              chartCardNode = parent;
              break;
            }
            parent = parent.parentElement;
          }
        }

        // Use ChartCard if found, otherwise fall back to boundaryRef
        const containerNode = chartCardNode || boundaryNode;
        const boundaryRect = containerNode?.getBoundingClientRect();

        if (
          gridRect &&
          anchorRect &&
          boundaryRect &&
          state.placement?.startsWith('right')
        ) {
          // Calculate position directly from table's right edge + 10px gap
          const tableRightEdge = gridRect.right;
          const gap = 10; // 10px gap from table edge
          const targetLeft = tableRightEdge + gap;

          // Get chart container dimensions (includes headers) from boundaryRef
          const chartTop = boundaryRect.top;
          const chartHeight = boundaryRect.height;

          const viewportWidth = window.innerWidth;
          const viewportRight = viewportWidth;

          // Keep a map sliver visible on the right at all sizes.
          const availableWidth = Math.max(
            0,
            viewportRight - targetLeft - VIEWPORT_PADDING
          );
          const mapPeekWidth = Math.min(
            POPOUT_MAP_PEEK_WIDTH,
            Math.floor(availableWidth * 0.2)
          );
          const maxWidthWithMapPeek = Math.max(
            0,
            availableWidth - mapPeekWidth
          );
          const calculatedMaxWidth = Math.min(
            POPOUT_PREFERRED_WIDTH,
            maxWidthWithMapPeek
          );
          setMaxWidth(calculatedMaxWidth);

          // Position popover to match chart container height and top (includes headers)
          const targetTop = chartTop;
          const targetHeight = chartHeight;

          // Calculate row center relative to chart container
          const rowCenter =
            anchorRect.top + (anchorRect.bottom - anchorRect.top) / 2;
          // Chevron position is relative to the top of the popover (which matches chart container top)
          const chevronPosition = rowCenter - chartTop;

          // Calculate row height
          const currentRowHeight = anchorRect.bottom - anchorRect.top;
          setRowHeight(currentRowHeight);

          // Ensure chevron stays within popover bounds (with some padding)
          const minChevronTop = 20; // Minimum distance from top
          const maxChevronTop = targetHeight - 20; // Minimum distance from bottom
          const clampedChevronTop = Math.max(
            minChevronTop,
            Math.min(maxChevronTop, chevronPosition)
          );

          setChevronTop(clampedChevronTop);
          setPopoverTop(targetTop);
          setPopoverHeight(targetHeight);

          // Apply the adjustment - set left, top, and height directly
          const appliedStyles = {
            left: `${targetLeft}px`,
            top: `${targetTop}px`,
            height: `${targetHeight}px`,
            width: `${calculatedMaxWidth}px`,
            maxWidth: `${calculatedMaxWidth}px`,
            transform: 'none', // Ensure no transform interferes
          };
          Object.assign(state.elements.popper.style, appliedStyles);
        }

        if (state.placement) {
          setPlacement(state.placement);
        }
      },
    };

    const popperInstance = createPopper(anchorRef.current, popperElement, {
      placement: 'right',
      modifiers: [adjustToTableEdgeModifier],
      onFirstUpdate: (state) => {
        if (state.placement) {
          setPlacement(state.placement);
        }
      },
    });

    popperInstanceRef.current = popperInstance;

    // Update popper position on scroll
    const handleScroll = () => {
      popperInstance.update();
    };

    const scrollContainer = gridRef?.getDOMNode();
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll, true);
      popperInstance.destroy();
      popperInstanceRef.current = null;
    };
  }, [isOpen, popperElement, gridRef, boundaryRef, anchorRef]);

  return {
    placement,
    chevronTop,
    maxWidth,
    popoverTop,
    popoverHeight,
    rowHeight,
  };
};
